import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const About = () => {
  return (
    <div className="w-full">
      <div className="bg-stone-100 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-block px-3 py-1 bg-purple-800 text-white rounded-full text-xs font-bold mb-4 uppercase tracking-widest">The Philosophy</div>
          <h1 className="font-serif text-4xl text-stone-800 mb-4">The Can Do Pathway</h1>
          <p className="text-lg text-stone-600 italic">Moving from 'not yet' to 'can do' across every stage of early childhood.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-16 space-y-16">
        {/* Born from Experience */}
        <section className="space-y-6">
          <h2 className="font-serif text-3xl text-purple-900">Born from Experience</h2>
          <div className="prose prose-lg text-gray-700 max-w-none">
            <p>
              After 10+ years as a Health Visitor serving families in <strong>Saddleworth and Greater Manchester</strong>, I saw how much stress standard developmental tools caused parents. These tools focus heavily on what a child <em>should</em> be doing but isn't—the deficits.
            </p>
            <p>
              I witnessed parents feeling like they were failing if a box wasn't ticked. I created <strong>The Can Do Method</strong> to change that conversation.
            </p>
          </div>
        </section>

        {/* The Stages Breakdown */}
        <section className="space-y-8">
          <h2 className="font-serif text-3xl text-purple-900 text-center">Our Developmental Pathway</h2>
          <div className="grid gap-6">
            <div className="bg-white p-6 rounded-xl border border-purple-100 shadow-sm flex flex-col md:flex-row gap-6 items-center">
              <div className="shrink-0 w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center text-purple-800 font-serif text-2xl italic font-bold">S1</div>
              <div>
                <h3 className="font-serif text-xl text-purple-900 font-semibold">Connect & Discover (0–1 years)</h3>
                <p className="text-gray-600 text-sm mt-2">Focusing on the vital bonding and early communication that happens in the first year. We explore sensory discovery and build foundational motor skills, moving at your baby's unique pace.</p>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-purple-100 shadow-sm flex flex-col md:flex-row gap-6 items-center">
              <div className="shrink-0 w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center text-purple-800 font-serif text-2xl italic font-bold">S2</div>
              <div>
                <h3 className="font-serif text-xl text-purple-900 font-semibold">Express & Explore (1–3 years)</h3>
                <p className="text-gray-600 text-sm mt-2">As your child begins to navigate their world, we focus on language development and social connection. We map their expanding gross and fine motor skills as they learn to express themselves.</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border border-purple-100 shadow-sm flex flex-col md:flex-row gap-6 items-center">
              <div className="shrink-0 w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center text-purple-800 font-serif text-2xl italic font-bold">S3</div>
              <div>
                <h3 className="font-serif text-xl text-purple-900 font-semibold">Grow & Get Ready (3–5 years)</h3>
                <p className="text-gray-600 text-sm mt-2">Focusing on imagination, confidence, and skill refinement. We prepare your child for school readiness by ensuring they have a secure sense of what they can do.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Pillars */}
        <section className="grid md:grid-cols-2 gap-12">
          <div className="flex flex-col justify-center space-y-6">
            <h3 className="font-serif text-2xl text-purple-900">The "Close To" Zone</h3>
            <p className="text-gray-600 leading-relaxed">
              Early brain development is most effective when it builds on existing neural pathways. By focusing on what a child is "close to" doing, we provide the right stimulation at the right time. This is called "Scaffolding"—and it's the heart of the Can Do Method.
            </p>
            <div className="p-4 bg-stone-50 border-l-4 border-purple-800 italic text-stone-700">
              "When we focus on what a child can do, we give them—and their parents—the confidence to explore what's next."
            </div>
          </div>
          <div className="bg-purple-900 text-white p-8 rounded-2xl space-y-6 shadow-lg">
            <h3 className="font-serif text-2xl border-b border-purple-700 pb-3">Our Core Pillars</h3>
            <ul className="space-y-4 text-sm">
              <li className="flex gap-3">
                <SafeIcon icon={FiIcons.FiEye} className="mt-1 shrink-0" />
                <span><strong>Observation First:</strong> Watching how your child interacts with their world, not just counting milestones.</span>
              </li>
              <li className="flex gap-3">
                <SafeIcon icon={FiIcons.FiAward} className="mt-1 shrink-0" />
                <span><strong>Strengths-Based:</strong> Starting with what they CAN do as the foundation for all future learning.</span>
              </li>
              <li className="flex gap-3">
                <SafeIcon icon={FiIcons.FiMap} className="mt-1 shrink-0" />
                <span><strong>Localized Presence:</strong> A grounded service for Saddleworth, Manchester, and Lancashire families.</span>
              </li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;